"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.webPublisherResultSchema = exports.webPublisherArgsSchema = exports.subagentTaskRunnerArgsSchema = exports.webPublishUpdateArgsSchema = exports.webPublishCreateArgsSchema = exports.webPublishContentChangeSchema = exports.webPublishContentDataSchema = exports.workflowNodeReferenceSchema = exports.seoArticleUpdaterNodeReferenceSchema = exports.seoArticleWriterNodeReferenceSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Workflow Node Reference Schema for SEO Article Writer (create actions)
exports.seoArticleWriterNodeReferenceSchema = zod_1.z.object({
    type: zod_1.z.literal('workflow_node_reference'),
    seoArticleWriterNodeId: zod_1.z
        .string()
        .describe('ID of the SEO Article Writer workflow node containing the content to publish. CRITICAL: This must be a node execution ID from seoArticleWriter tool only, never from seoArticleUpdater or other tools.'),
});
// Workflow Node Reference Schema for SEO Article Updater (update actions)
exports.seoArticleUpdaterNodeReferenceSchema = zod_1.z.object({
    type: zod_1.z.literal('workflow_node_reference'),
    seoArticleUpdaterNodeId: zod_1.z
        .string()
        .describe('ID of the SEO Article Updater workflow node containing the changes to apply. CRITICAL: This must be a node execution ID from seoArticleUpdater tool only, never from seoArticleWriter or other tools.'),
});
// Legacy workflow node reference schema - deprecated, use specific schemas above
exports.workflowNodeReferenceSchema = zod_1.z.object({
    type: zod_1.z.literal('workflow_node_reference'),
    nodeId: zod_1.z.string().describe('ID of the workflow node containing the data to use'),
});
// Web Publish Content Data Schema - core content structure
exports.webPublishContentDataSchema = zod_1.z.object({
    title: zod_1.z.string().nullish().describe('Page/article title'),
    slug: zod_1.z.string().describe('URL slug for the page/article'),
    metaTitle: zod_1.z.string().nullish().describe('SEO meta title'),
    metaDescription: zod_1.z.string().nullish().describe('SEO meta description'),
    body: zod_1.z.string().describe('Main content body (HTML, markdown, or plaintext)'),
    keywords: zod_1.z.array(zod_1.z.string()).nullish().describe('Target keywords'),
});
// Web Publish Content Change Schema - for content updates
exports.webPublishContentChangeSchema = common_1.contentChangeSchema.extend({
    changeReason: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Explanation of why this change improves content/SEO'),
});
// Web Publish Create Args Schema - for creating new content
exports.webPublishCreateArgsSchema = zod_1.z.union([
    // Flat interface with seoArticleWriterNodeId
    zod_1.z.object({
        pageType: zod_1.z
            .enum(['page', 'blog'])
            .optional()
            .nullable()
            .describe('Type of content to create (optional, will be inferred if not provided)'),
        seoArticleWriterNodeId: zod_1.z
            .string()
            .describe('ID of the SEO Article Writer workflow node containing the content to publish. CRITICAL: This must be a node execution ID from seoArticleWriter tool only.'),
    }),
    // Interface with content data
    zod_1.z.object({
        pageType: zod_1.z.enum(['page', 'blog']).nullish().describe('Type of content to create'),
        publishTarget: zod_1.z
            .enum(['page', 'blog'])
            .optional()
            .nullable()
            .describe('Publishing target - alias for pageType'),
        contentType: zod_1.z
            .string()
            .optional()
            .nullable()
            .describe('Content type - used for pageType inference'),
        content: zod_1.z
            .union([
            exports.seoArticleWriterNodeReferenceSchema,
            exports.webPublishContentDataSchema,
            zod_1.z.string().describe('Raw content as text'),
        ])
            .optional()
            .nullable()
            .describe('Content data - MUST use seoArticleWriterNodeId when referencing SEO Article Writer workflow node output. This ensures only SEO Article Writer content is used for content creation.'),
        contentSource: zod_1.z
            .union([
            exports.seoArticleWriterNodeReferenceSchema,
            exports.webPublishContentDataSchema,
            zod_1.z.string().describe('Raw content as text'),
        ])
            .optional()
            .nullable()
            .describe('Content source data - alias for content field. MUST use seoArticleWriterNodeId when referencing SEO Article Writer workflow node output.'),
    }),
]);
// Web Publish Update Args Schema - for updating existing content
exports.webPublishUpdateArgsSchema = zod_1.z.union([
    // Flat interface with seoArticleUpdaterNodeId
    zod_1.z.object({
        pageType: zod_1.z
            .enum(['page', 'blog'])
            .optional()
            .nullable()
            .describe('Type of content to update (optional, will be inferred if not provided)'),
        seoArticleUpdaterNodeId: zod_1.z
            .string()
            .describe('ID of the SEO Article Updater workflow node containing the changes to apply. CRITICAL: This must be a node execution ID from seoArticleUpdater tool only. The target slug will be automatically extracted from the updater result.'),
    }),
    // Interface with changes array
    zod_1.z.object({
        pageType: zod_1.z.enum(['page', 'blog']).describe('Type of content to update'),
        changes: zod_1.z
            .union([
            exports.seoArticleUpdaterNodeReferenceSchema,
            zod_1.z.array(exports.webPublishContentChangeSchema),
            zod_1.z.string().describe('Raw changes description as text'),
        ])
            .describe('Change data - MUST use seoArticleUpdaterNodeId when referencing SEO Article Updater workflow node output. This ensures only SEO Article Updater changes are used for content updates. The target slug will be automatically extracted from the updater result.'),
        metaUpdates: zod_1.z
            .object({
            title: zod_1.z.string().nullish(),
            metaTitle: zod_1.z.string().nullish(),
            metaDescription: zod_1.z.string().nullish(),
            keywords: zod_1.z.array(zod_1.z.string()).nullish(),
        })
            .optional()
            .nullable()
            .describe('Optional metadata updates'),
    }),
]);
// Subagent Task Runner Args Schema - for natural language tasks
exports.subagentTaskRunnerArgsSchema = zod_1.z
    .string()
    .describe('Natural language instructions for miscellaneous publishing tasks with direct API access');
// Base Web Publisher Args Schema - defines the 3 core actions
exports.webPublisherArgsSchema = zod_1.z.object({
    action: zod_1.z
        .enum(['create', 'update', 'subagentTaskRunner'])
        .describe('CRITICAL TOOL ROUTING: create (ONLY with SEO Article Writer node references) - create new page at a URL from seoArticleWriter output, update (ONLY with SEO Article Updater node references) - update existing page content from seoArticleUpdater output, subagentTaskRunner - natural language prompt for miscellaneous publishing task (pass content by value, never use node execution IDs)'),
    args: zod_1.z
        .union([exports.webPublishCreateArgsSchema, exports.webPublishUpdateArgsSchema, exports.subagentTaskRunnerArgsSchema])
        .describe('MANDATORY NODE TYPE RESTRICTIONS: For create action - MUST use seoArticleWriterNodeId to reference SEO Article Writer workflow nodes. For update action - MUST use seoArticleUpdaterNodeId to reference SEO Article Updater workflow nodes. For subagentTaskRunner: natural language instructions only, pass content by value, never use node execution IDs.'),
});
// Base Web Publisher Result Schema
exports.webPublisherResultSchema = zod_1.z.object({
    summary: zod_1.z.string().describe('Summary of what was accomplished'),
    url: zod_1.z.string().nullish().describe('URL or domain to view the created/updated content'),
    changes: zod_1.z
        .array(zod_1.z.object({
        action: zod_1.z.string().describe('Action taken'),
        target: zod_1.z.string().describe('What was targeted (article, page, product, etc.)'),
        details: zod_1.z.string().describe('Specific details about the change'),
    }))
        .describe('List of changes made'),
});
